import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Share2, 
  TrendingUp,
  TrendingDown,
  Clock, 
  Target,
  Brain,
  Activity,
  Timer,
  BookOpen,
  CalendarDays
} from "lucide-react";
import { TimeSpentChart } from "@/components/analytics/TimeSpentChart";
import { AccuracyTrendChart } from "@/components/analytics/AccuracyTrendChart";
import { StudyHeatmap } from "@/components/analytics/StudyHeatmap";
import { TopicComparisonRadar } from "@/components/analytics/TopicComparisonRadar";
import { TopicMasteryChart } from "@/components/analytics/TopicMasteryChart";
import { ExamReadinessGauge } from "@/components/analytics/ExamReadinessGauge";
import { AnalyticsExport } from "@/components/analytics/AnalyticsExport";
import { CalibrationWidget } from "@/components/analytics/CalibrationWidget";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo } from "react";

export default function Analytics() {
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: analytics, isLoading } = useQuery({
    queryKey: ["user-analytics", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-user-analytics", {
        body: { userId: user?.id, timeRange: "all" }
      });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Improvement banner calculation
  const improvement = useMemo(() => {
    const trend = analytics?.accuracyTrend || [];
    if (trend.length < 4) return null;
    const mid = Math.floor(trend.length / 2);
    const firstHalf = trend.slice(0, mid).filter((d: any) => d.accuracy > 0);
    const secondHalf = trend.slice(mid).filter((d: any) => d.accuracy > 0);
    if (firstHalf.length === 0 || secondHalf.length === 0) return null;
    const avgFirst = firstHalf.reduce((s: number, d: any) => s + d.accuracy, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((s: number, d: any) => s + d.accuracy, 0) / secondHalf.length;
    const diff = Math.round(avgSecond - avgFirst);
    return { diff, improved: diff >= 0 };
  }, [analytics?.accuracyTrend]);

  // 90-day heatmap slice
  const heatmap90 = useMemo(() => {
    const full = analytics?.studyHeatmap || [];
    return full.slice(-90);
  }, [analytics?.studyHeatmap]);

  // Derived metrics
  const totalActiveDays = analytics?.totalActiveDays ?? 
    (analytics?.studyHeatmap || []).filter((d: any) => d.count > 0).length;
  const totalAttempts = analytics?.totalAttempts ?? 0;
  const avgTimePerQuestion = totalAttempts > 0 
    ? Math.round((analytics?.totalStudyTimeSeconds || 0) / totalAttempts)
    : 0;
  const topicsPracticed = (analytics?.topicComparison || []).length;

  const handleShare = async () => {
    toast({ title: "Generating progress card...", description: "Creating your shareable image" });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <>
      <SEO title="Personal Analytics" description="Track your study progress with comprehensive analytics" />

      <div className="space-y-6">
        <nav className="flex flex-wrap gap-2 border-b pb-3" aria-label="Performance sections">
          <Button asChild variant="default" size="sm">
            <a href="/analytics">Performance</a>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <a href="/study-plan">Study Plan</a>
          </Button>
        </nav>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Personal Analytics</h1>
            <p className="text-muted-foreground mt-1">Deep insights into your learning journey</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Share Progress
            </Button>
            <AnalyticsExport analytics={analytics} />
          </div>
        </div>

        {/* Improvement Banner */}
        {improvement && improvement.diff !== 0 && (
          <Card className={improvement.improved ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"}>
            <CardContent className="flex items-center gap-3 py-4">
              {improvement.improved ? (
                <TrendingUp className="h-5 w-5 text-green-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
              <p className="text-sm font-medium">
                {improvement.improved
                  ? `You've improved ${improvement.diff}% compared to your earlier sessions — keep it up!`
                  : `Your accuracy dipped ${Math.abs(improvement.diff)}% — review your weak areas to bounce back.`}
              </p>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards — 6 cards in 3x2 grid */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Study Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.floor((analytics?.totalStudyTimeSeconds || 0) / 3600)}h</div>
              <p className="text-xs text-muted-foreground">This month: {Math.floor((analytics?.monthlyStudyTimeSeconds || 0) / 3600)}h</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Exam Readiness</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.examReadinessScore || 0}%</div>
              <p className="text-xs text-muted-foreground">Based on performance trends</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Topics Mastered</CardTitle>
              <Brain className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.topicsMastered || 0}</div>
              <p className="text-xs text-muted-foreground">Accuracy ≥ 85%</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Study Streak</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.currentStreak || 0} days</div>
              <p className="text-xs text-muted-foreground">Best: {analytics?.longestStreak || 0} days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Avg Time/Question</CardTitle>
              <Timer className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{avgTimePerQuestion}s</div>
              <p className="text-xs text-muted-foreground">{totalAttempts} total attempts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Days</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalActiveDays}</div>
              <p className="text-xs text-muted-foreground">{topicsPracticed} topics practiced</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="topics">By Topic</TabsTrigger>
            <TabsTrigger value="time">Time Analysis</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <AccuracyTrendChart data={analytics?.accuracyTrend || []} />
              <ExamReadinessGauge score={analytics?.examReadinessScore || 0} />
            </div>
            <StudyHeatmap data={heatmap90} label="Daily study activity over the last 90 days" />
            <CalibrationWidget />
          </TabsContent>

          <TabsContent value="topics" className="space-y-4">
            <TopicMasteryChart data={analytics?.topicComparison || []} />
            <TopicComparisonRadar data={analytics?.topicComparison || []} />
            <TimeSpentChart data={analytics?.timePerTopic || []} type="bar" />
          </TabsContent>

          <TabsContent value="time" className="space-y-4">
            <TimeSpentChart data={analytics?.timePerTopic || []} type="bar" />
            <Card>
              <CardHeader><CardTitle>Study Patterns</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Peak Study Time</p>
                    <p className="text-xl font-semibold">{analytics?.peakStudyTime || "Not enough data"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Average Session Length</p>
                    <p className="text-xl font-semibold">{Math.round((analytics?.avgSessionLength || 0) / 60)} minutes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trends" className="space-y-4">
            <AccuracyTrendChart data={analytics?.accuracyTrend || []} />
            <Card>
              <CardHeader><CardTitle>Performance Insights</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analytics?.insights?.map((insight: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-primary mt-0.5" />
                      <span className="text-sm">{insight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
